import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
} from '@mui/material';
import { getAssignedProjects } from '../../services/projectService';
import { getCurrentUser, getUserPtoHours } from '../../services/userService';
import { useAuth } from '../../context/AuthContext';

const CreateTimecardDialog = ({ 
    open, 
    onClose, 
    currentUser, 
    onCreateTimecard,
    usedProjectIds 
}) => {
    const { user } = useAuth();
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState('');
    const [ptoBalance, setPtoBalance] = useState(null);

    // Fetch user's PTO balance
    const fetchPtoBalance = useCallback(async () => {
        try {
            const userData = await getCurrentUser();
            const ptoHours = await getUserPtoHours(userData._id);
            
            // Calculate available PTO hours
            const available = (
                parseFloat(ptoHours.yearStart || 0) +
                parseFloat(ptoHours.ytdAccumulated || 0) -
                parseFloat(ptoHours.ytdUsed || 0)
            ).toFixed(2);
            
            setPtoBalance(parseFloat(available));
        } catch (error) {
            console.error('Error fetching PTO balance:', error);
            setPtoBalance(null);
        }
    }, []);

    const fetchUserProjects = useCallback(async () => {
        try {
            console.log('Fetching projects for user:', currentUser);
            setProjects([]); // Clear existing projects while loading
            
            const userProjects = await getAssignedProjects();
            console.log('Raw user projects:', userProjects);
            
            // Filter out any projects that are already used
            const availableProjects = userProjects.filter(
                project => !usedProjectIds.includes(project._id)
            );
            console.log('Available projects after filtering:', availableProjects);
            
            // Get user's default project ID
            const defaultProjectId = user?.defaultProject?._id || user?.defaultProject;
            console.log('User default project ID:', defaultProjectId);
            
            // Sort projects: default project first, then alphabetically
            const sortedProjects = availableProjects.sort((a, b) => {
                // If 'a' is the default project, it should come first
                if (a._id === defaultProjectId) return -1;
                // If 'b' is the default project, it should come first
                if (b._id === defaultProjectId) return 1;
                // Otherwise, sort alphabetically
                return a.name.localeCompare(b.name);
            });
            console.log('Final sorted projects:', sortedProjects);
            
            setProjects(sortedProjects);
        } catch (error) {
            console.error('Error fetching user projects:', error);
            console.error('Error details:', error.response?.data || error.message);
            setProjects([]); // Clear projects on error
        }
    }, [currentUser, usedProjectIds, user]);

    useEffect(() => {
        if (open && currentUser) {
            fetchUserProjects();
            fetchPtoBalance();
        } else {
            // Clear projects and selection when dialog closes
            setProjects([]);
            setSelectedProject('');
        }
    }, [open, currentUser, fetchUserProjects, fetchPtoBalance]);

    // Auto-select default project when projects are loaded
    useEffect(() => {
        if (projects.length > 0 && user?.defaultProject && !selectedProject) {
            const defaultProjectId = user.defaultProject._id || user.defaultProject;
            
            // Check if the default project is available in the current projects list
            const isDefaultProjectAvailable = projects.some(project => project._id === defaultProjectId);
            
            if (isDefaultProjectAvailable) {
                // Check if it's a PTO project and user has no balance
                const defaultProject = projects.find(project => project._id === defaultProjectId);
                const isPtoProject = defaultProject && (defaultProject.name.includes('Leave') || defaultProject.name.includes('ADMIN_PTO_SSL_LEAVE'));
                const hasNoPtoBalance = ptoBalance !== null && ptoBalance <= 0;
                
                // Only auto-select if it's not a PTO project with zero balance
                if (!isPtoProject || !hasNoPtoBalance) {
                    console.log('Auto-selecting default project:', defaultProject?.name);
                    setSelectedProject(defaultProjectId);
                }
            }
        }
    }, [projects, user, selectedProject, ptoBalance]);

    const handleProjectChange = (event) => {
        setSelectedProject(event.target.value);
    };

    const handleCreate = () => {
        const project = projects.find(p => p._id === selectedProject);
        if (project) {
            onCreateTimecard(project);
        }
    };

    return (
        <Dialog
            open={open}
            disableRestoreFocus
            onClose={(event, reason) => {
                // Clear focus before closing
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                onClose();
            }}
            maxWidth="sm"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    bgcolor: 'background.paper',
                },
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
            }}
        >
            <DialogTitle>
                Create Timecard
            </DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ mt: 2 }} size="small">
                    <InputLabel>Select Project</InputLabel>
                    <Select
                        value={selectedProject}
                        onChange={handleProjectChange}
                        label="Select Project"
                    >
                        {projects.map((project) => {
                            // Check if this is a Leave or ADMIN_PTO_SSL_LEAVE project
                            const isPtoProject = project.name.includes('Leave') || project.name.includes('ADMIN_PTO_SSL_LEAVE');
                            // Disable PTO projects if balance is 0
                            const isDisabled = isPtoProject && ptoBalance !== null && ptoBalance <= 0;
                            
                            return (
                                <MenuItem 
                                    key={project._id}
                                    value={project._id}
                                    disabled={isDisabled}
                                    title={isDisabled ? "No PTO hours available" : ""}
                                >
                                    {project.name}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={(e) => {
                        e.currentTarget.blur();
                        onClose();
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={(e) => {
                        e.currentTarget.blur();
                        handleCreate();
                    }}
                    variant="contained" 
                    color="primary"
                    disabled={!selectedProject}
                >
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateTimecardDialog;
